import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Save, ShieldAlert, Moon, Sunrise, X } from "lucide-react";
import { http, trackEvent } from "@/lib/api";
import { Card, PageTitle, btnPrimary, btnGhost, fieldCls, StatusPill } from "@/components/portal/ui";

// Dynamic conditional intake. Autosaves to the account (not browser). Save & resume across devices.
const CONCERNS = [
  ["constant_waking", "My baby wakes constantly"], ["short_naps", "Naps last 20-30 minutes"],
  ["crib", "My baby won't sleep in the crib"], ["bedtime", "Bedtime takes forever"],
  ["regression", "Everything suddenly changed"], ["unsure", "I don't know what's wrong"],
];

function Field({ label, children }) {
  return <div><label className="block text-sm text-[#6B665E] mb-2">{label}</label>{children}</div>;
}
function Radio({ name, value, options, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)}
          className={`px-4 py-2 rounded-full text-sm border transition-colors ${value === o ? "bg-[#9F634E] border-[#9F634E] text-white" : "border-[#E5E1D8] text-[#1A1918] hover:border-[#9F634E]/50"}`}>{o}</button>
      ))}
    </div>
  );
}

export default function Intake() {
  const nav = useNavigate();
  const [a, setA] = useState({});
  const [status, setStatus] = useState("Not Started");
  const [savedAt, setSavedAt] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const timer = useRef();

  useEffect(() => {
    trackEvent("clarity_intake_started", {}, { once: true });
    http.get("/portal/intake").then(async (r) => {
      if (r.data.entitled === false) {
        toast.error("Start your Infant Sleep Diagnostic to open the Clarity intake.");
        nav("/portal");
        return;
      }
      setA(r.data.answers || {});
      setStatus(r.data.status || "Not Started");
      if ((r.data.status === "In Progress") && Object.keys(r.data.answers || {}).length > 0) {
        trackEvent("clarity_intake_resume", {});
      }
      let tl = r.data.timeline || null;
      const raw = (() => { try { return localStorage.getItem("isc_timeline"); } catch (e) { return null; } })();
      if (!tl && raw) {
        try {
          const parsed = JSON.parse(raw);
          await http.put("/portal/intake/timeline", { timeline: parsed });
          tl = parsed;
          trackEvent("timeline_to_clarity_purchase", {});
          const r2 = await http.get("/portal/intake");
          setA(r2.data.answers || {});
          setStatus(r2.data.status || "In Progress");
        } catch (e) {}
      }
      try { localStorage.removeItem("isc_timeline"); } catch (e) {}
      setTimeline(tl);
    }).catch(() => {});
  }, []);

  const removeTimeline = async () => {
    try { await http.put("/portal/intake/timeline", { timeline: {} }); } catch (e) {}
    setTimeline(null);
    toast.success("Timeline removed");
  };

  const set = (k, v) => {
    setA((prev) => {
      const next = { ...prev, [k]: v };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => autosave(next), 700);
      return next;
    });
  };
  const autosave = async (answers) => {
    try { await http.put("/portal/intake", { answers }); setSavedAt(new Date()); }
    catch (e) { toast.error("We couldn't save your latest answers — please check your connection."); }
  };
  const completedRef = useRef(false);
  const submit = async () => {
    try {
      // Submit the current answers atomically so a debounced/failed autosave can never
      // leave the consultant without what the parent actually completed.
      const { data } = await http.post("/portal/intake/submit", { answers: a });
      setSavedAt(new Date());
      if (!completedRef.current) { completedRef.current = true; trackEvent("clarity_intake_completed", {}); }
      if (data.flags?.length) toast.warning("We've flagged some responses for careful professional review.");
      else toast.success("Submitted for professional review");
      nav("/portal");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not submit");
    }
  };

  const concern = a.primary_concern;
  const submitted = ["Submitted", "Awaiting Operator Review", "Under Review", "Delivered", "Viewed", "Completed"].includes(status);

  if (submitted) {
    return (
      <div>
        <PageTitle eyebrow="Diagnostic" title="Intake submitted" />
        <Card className="p-8 max-w-2xl text-center">
          <CheckCircle2 className="mx-auto text-[#667A66]" size={44} />
          <p className="font-display text-2xl text-[#1A1918] mt-4">Nothing else needed right now.</p>
          <p className="text-[#6B665E] mt-2">Your information has been submitted for professional review. We'll notify your portal when your Infant Sleep Clarity Report is ready.</p>
          <div className="mt-5"><StatusPill state={status} /></div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageTitle eyebrow="Diagnostic · focused questionnaire" title="Tell us what's happening"
        sub="A quick, focused questionnaire — just enough for your Certified Infant Sleep Consultant to understand the main problem. This is intentionally lighter than the full Blueprint intake. Your progress saves automatically." />
      <div className="flex items-center gap-2 text-sm text-[#8A998C] mb-5">
        <Save size={14} /> {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Your progress is saved automatically"}
      </div>

      <div className="space-y-4 max-w-2xl">
        {timeline && (timeline.bedtime || (timeline.wakings || []).length > 0) && (
          <Card className="p-6" data-testid="intake-timeline-review">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow text-[#8A998C] mb-1 flex items-center gap-1.5"><Moon size={13} className="text-[#9F634E]" /> Last Night's Timeline</div>
                <p className="text-[#6B665E] text-sm">Imported from what you mapped. Review &amp; confirm — you can update the details below.</p>
              </div>
              <button onClick={removeTimeline} data-testid="intake-timeline-remove"
                className="shrink-0 text-[#8A998C] hover:text-[#9E4F4F] transition-colors flex items-center gap-1 text-xs">
                <X size={14} /> Remove
              </button>
            </div>
            <div className="mt-4 space-y-1.5 text-sm">
              {timeline.bedtime && (
                <div className="flex justify-between border-b border-[#F0ECE4] py-1.5"><span className="text-[#6B665E]">Bedtime</span><span className="text-[#1A1918]">{timeline.bedtime}</span></div>
              )}
              {(timeline.wakings || []).map((w, i) => (
                <div key={i} className="flex justify-between border-b border-[#F0ECE4] py-1.5">
                  <span className="text-[#6B665E]">Wake {i + 1}</span>
                  <span className="text-[#1A1918]">{w.time}{w.settle ? ` — ${w.settle}` : ""}</span>
                </div>
              ))}
              {timeline.morning_wake && (
                <div className="flex justify-between py-1.5"><span className="text-[#6B665E] flex items-center gap-1.5"><Sunrise size={13} className="text-[#9F634E]" /> Morning wake</span><span className="text-[#1A1918]">{timeline.morning_wake}</span></div>
              )}
            </div>
          </Card>
        )}

        <Card className="p-6 space-y-4">
          <Field label="Baby's age (months)"><input data-testid="intake-age" type="number" className={fieldCls} value={a.age_months || ""} onChange={(e) => set("age_months", e.target.value)} /></Field>
          <Field label="What brought you here tonight?">
            <div className="grid sm:grid-cols-2 gap-2">
              {CONCERNS.map(([k, label]) => (
                <button key={k} type="button" data-testid={`intake-concern-${k}`} onClick={() => set("primary_concern", k)}
                  className={`px-4 py-2.5 rounded-lg text-sm text-left border transition-colors ${concern === k ? "bg-[#9F634E]/10 border-[#9F634E] text-[#9F634E]" : "border-[#E5E1D8] text-[#1A1918] hover:border-[#9F634E]/40"}`}>{label}</button>
              ))}
            </div>
          </Field>
        </Card>

        {concern && (
          <Card className="p-6 space-y-5">
            <Field label="Current bedtime"><input className={fieldCls} placeholder="e.g. 8:30pm" value={a.bedtime || ""} onChange={(e) => set("bedtime", e.target.value)} /></Field>
            <Field label="How does your baby usually fall asleep?">
              <Radio value={a.falls_asleep} options={["Fed to sleep", "Rocked/held", "Independently", "Varies"]} onChange={(v) => set("falls_asleep", v)} />
            </Field>
            {(concern === "constant_waking" || concern === "unsure") && (
              <>
                <Field label="Roughly how many night wakings?"><input type="number" data-testid="intake-wakings" className={fieldCls} value={a.wakings || ""} onChange={(e) => set("wakings", e.target.value)} /></Field>
                <Field label="What usually resolves a waking?"><Radio value={a.resolve} options={["Feeding", "Rocking", "Replacing pacifier", "Resettles alone", "Varies"]} onChange={(v) => set("resolve", v)} /></Field>
              </>
            )}
            {(concern === "short_naps" || concern === "unsure") && (
              <>
                <Field label="How many naps per day?"><input type="number" className={fieldCls} value={a.naps || ""} onChange={(e) => set("naps", e.target.value)} /></Field>
                <Field label="Typical nap length"><Radio value={a.nap_length} options={["<30 min", "30-45 min", "45-90 min", "Over 90 min"]} onChange={(v) => set("nap_length", v)} /></Field>
              </>
            )}
            {(concern === "crib") && (
              <Field label="Where does your baby sleep best right now?"><Radio value={a.sleeps_where} options={["In arms", "Contact/bed", "Crib briefly", "Crib well"]} onChange={(v) => set("sleeps_where", v)} /></Field>
            )}
            {(concern === "bedtime") && (
              <Field label="How long does bedtime take?"><Radio value={a.bedtime_len} options={["<20 min", "20-45 min", "45-90 min", "Over 90 min"]} onChange={(v) => set("bedtime_len", v)} /></Field>
            )}
            {(concern === "regression") && (
              <Field label="When did things change?"><input className={fieldCls} placeholder="e.g. about 2 weeks ago, around 4 months" value={a.changed_when || ""} onChange={(e) => set("changed_when", e.target.value)} /></Field>
            )}
          </Card>
        )}

        {concern && (
          <Card className="p-6 space-y-5">
            <Field label="Sleep environment"><Radio value={a.environment} options={["Dark + white noise", "Some light", "Room sharing", "Own room"]} onChange={(v) => set("environment", v)} /></Field>
            <Field label="What have you already tried?"><textarea rows={3} className={fieldCls} value={a.tried || ""} onChange={(e) => set("tried", e.target.value)} /></Field>
            <Field label="What's your main goal?"><textarea rows={2} data-testid="intake-goal" className={fieldCls} value={a.goals || ""} onChange={(e) => set("goals", e.target.value)} /></Field>
            <div>
              <label className="block text-sm text-[#6B665E] mb-2 flex items-center gap-2"><ShieldAlert size={15} className="text-[#C49A5A]" /> Any health, feeding, breathing or growth concerns?</label>
              <textarea rows={2} data-testid="intake-medical" className={fieldCls} placeholder="If yes, please describe. We'll flag this for careful review and may recommend your pediatrician." value={a.medical || ""} onChange={(e) => set("medical", e.target.value)} />
            </div>
          </Card>
        )}

        <div className="flex gap-3">
          <button data-testid="intake-save" onClick={() => autosave(a).then(() => toast.success("Progress saved"))} className={btnGhost}>Save & continue later</button>
          <button data-testid="intake-submit" onClick={submit} disabled={!concern} className={btnPrimary}>Submit for professional review</button>
        </div>
      </div>
    </div>
  );
}
